<template>
  <div class="reports">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('portal.navReports') }}</h1>
        <p class="page-sub">{{ t('reports.sub') }}</p>
      </div>
      <button class="btn btn-ghost" :disabled="!filteredTasks.length" @click="exportReport">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        {{ t('common.export') }}
      </button>
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="projects.length === 0" class="empty-state"><p>{{ t('portal.noProjects') }}</p></div>

    <template v-else>
      <div class="filter-row">
        <select v-model="projectId" class="form-control proj-sel">
          <option :value="0">{{ t('reports.allProjects') }}</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>

      <!-- Billing summary -->
      <div class="bill-strip">
        <div class="bill"><div class="bv">{{ totalHours }}h</div><div class="bl">{{ t('portal.hoursTracked') }}</div></div>
        <div class="bill"><div class="bv">{{ doneCount }}/{{ filteredTasks.length }}</div><div class="bl">{{ t('reports.tasksDone') }}</div></div>
        <div class="bill"><div class="bv">{{ subDone }}/{{ subTotal }}</div><div class="bl">{{ t('reports.subtasksDone') }}</div></div>
        <div class="bill"><div class="bv">{{ totalDue }}</div><div class="bl">{{ t('reports.totalDue') }}</div></div>
      </div>

      <!-- Charts -->
      <div class="charts">
        <div class="card">
          <div class="card-title">{{ t('reports.hoursPerTask') }}</div>
          <ChartCanvas v-if="hoursChart.labels.length" type="bar" :labels="hoursChart.labels" :data="hoursChart.data" :label="t('portal.hoursTracked')" />
          <p v-else class="muted">{{ t('reports.noData') }}</p>
        </div>
        <div class="card">
          <div class="card-title">{{ t('reports.subtasksChart') }}</div>
          <ChartCanvas v-if="subTotal > 0" type="doughnut" :labels="[t('reports.done'), t('reports.remaining')]" :data="[subDone, Math.max(0, subTotal - subDone)]" />
          <p v-else class="muted">{{ t('reports.noData') }}</p>
        </div>
        <div class="card">
          <div class="card-title">{{ t('reports.billingPerProject') }}</div>
          <ChartCanvas v-if="billChart.labels.length" type="bar" :labels="billChart.labels" :data="billChart.data" :label="t('reports.totalDue')" money />
          <p v-else class="muted">{{ t('reports.noData') }}</p>
        </div>
      </div>

      <!-- Tasks table -->
      <div class="card table-card">
        <table class="rep-table">
          <thead>
            <tr>
              <th>{{ t('export.task') }}</th>
              <th>{{ t('export.project') }}</th>
              <th>{{ t('export.status') }}</th>
              <th class="num">{{ t('export.hours') }}</th>
              <th class="num">{{ t('subtasks.toggle') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tk in filteredTasks" :key="tk.id">
              <td>{{ tk.title }}</td>
              <td class="muted">{{ tk.project_name || '—' }}</td>
              <td><span :class="['badge', 'badge-' + tk.status]">{{ t('status.' + tk.status) }}</span></td>
              <td class="num">{{ hrs(tk.total_seconds) }}</td>
              <td class="num">{{ tk.subtask_done }}/{{ tk.subtask_total }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import ChartCanvas from '@/components/ChartCanvas.vue'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney, projectTotal } from '@/utils/billing'
import { exportCsv, secondsToHours } from '@/utils/exportCsv'

export default {
  name: 'ClientReports',
  components: { ChartCanvas },
  setup() {
    const { t } = useI18n()
    const projects = ref([])
    const tasks = ref([])
    const loading = ref(true)
    const projectId = ref(0)

    const filteredTasks = computed(() => projectId.value ? tasks.value.filter(tk => tk.project_id === projectId.value) : tasks.value)
    const currency = computed(() => projects.value.find(p => p.currency)?.currency || 'ILS')

    const totalHours = computed(() => Math.round(filteredTasks.value.reduce((s, tk) => s + (tk.total_seconds || 0), 0) / 3600 * 10) / 10)
    const doneCount  = computed(() => filteredTasks.value.filter(tk => tk.status === 'done').length)
    const subDone  = computed(() => filteredTasks.value.reduce((s, tk) => s + (tk.subtask_done || 0), 0))
    const subTotal = computed(() => filteredTasks.value.reduce((s, tk) => s + (tk.subtask_total || 0), 0))
    const totalDue = computed(() => {
      const projs = projectId.value ? projects.value.filter(p => p.id === projectId.value) : projects.value
      const sum = projs.reduce((s, p) => s + (projectTotal(p) || 0), 0)
      return fmtMoney(sum, currency.value)
    })

    const hoursChart = computed(() => {
      const tk = filteredTasks.value.filter(t => (t.total_seconds || 0) > 0).slice(0, 12)
      return { labels: tk.map(t => t.title), data: tk.map(t => secondsToHours(t.total_seconds)) }
    })
    const billChart = computed(() => {
      const projs = projects.value.filter(p => projectTotal(p) != null)
      return { labels: projs.map(p => p.name), data: projs.map(p => projectTotal(p)) }
    })

    const hrs = (s) => secondsToHours(s) + 'h'

    function exportReport() {
      const rows = filteredTasks.value.map(tk => ({
        [t('export.task')]:    tk.title,
        [t('export.project')]: tk.project_name || '',
        [t('export.status')]:  t('status.' + tk.status),
        [t('export.hours')]:   secondsToHours(tk.total_seconds),
        [t('reports.subtasksDone')]: `${tk.subtask_done}/${tk.subtask_total}`,
      }))
      exportCsv(rows, `devtask-report-${new Date().toISOString().slice(0, 10)}.csv`)
    }

    async function load() {
      loading.value = true
      try {
        const [pr, tr] = await Promise.all([api.getProjects(), api.getTasks()])
        projects.value = pr.data || []
        tasks.value = tr.data || []
      } catch {} finally { loading.value = false }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('task.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return {
      t, projects, loading, projectId, filteredTasks, totalHours, doneCount, subDone, subTotal, totalDue,
      hoursChart, billChart, hrs, exportReport,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.muted { color: var(--muted); font-size: 13px; }
.filter-row { margin-bottom: 18px; }
.proj-sel { max-width: 280px; }

.bill-strip { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); overflow: hidden; margin-bottom: 22px; }
.bill { padding: 14px 18px; }
.bill + .bill { border-inline-start: 1px solid var(--border); }
.bv { font-size: 24px; font-weight: 600; font-variant-numeric: tabular-nums; }
.bl { font-size: 11px; color: var(--muted); margin-top: 5px; }

.charts { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; margin-bottom: 22px; }
.card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; }
.card-title { font-size: 13px; font-weight: 600; margin-bottom: 12px; }

.table-card { padding: 0; overflow: auto; }
.rep-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.rep-table th { text-align: start; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: var(--muted); font-weight: 600; padding: 12px 16px; border-bottom: 1px solid var(--border); }
.rep-table td { padding: 12px 16px; border-bottom: 1px solid var(--border); }
.rep-table tr:last-child td { border-bottom: none; }
.rep-table .num { text-align: end; font-variant-numeric: tabular-nums; }

@media (max-width: 768px) {
  .bill-strip { grid-template-columns: repeat(2, 1fr); }
  .bill:nth-child(3) { border-inline-start: none; }
}
</style>
